import apiClient from './client';

/* ---------- Types ---------- */

export interface ReviewStepConfig {
  id: string;
  workflow_id: string;
  order: number;
  name: string;
  required_role: string;
  required_approvals: number;
  description?: string | null;
}

export interface WorkflowConfig {
  id: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
  steps: ReviewStepConfig[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface StepApproval {
  id: string;
  case_review_step_id: string;
  approved_by: string;
  decision: 'approve' | 'return' | 'escalate';
  rationale?: string | null;
  created_at: string;
}

export interface CaseReviewStep {
  id: string;
  case_id: string;
  workflow_id: string;
  step_config_id: string;
  order: number;
  status: 'pending' | 'in_review' | 'approved' | 'returned' | 'skipped';
  assigned_reviewer_id?: string | null;
  approvals: StepApproval[];
}

export interface CaseWorkflow {
  workflow: WorkflowConfig;
  steps: CaseReviewStep[];
  current_step_order: number;
}

export interface ChecklistItem {
  id: string;
  case_id: string;
  label: string;
  status: 'required' | 'provided' | 'missing' | 'requested';
  flagged_by?: string | null;
  flagged_at?: string | null;
  notes?: string | null;
  sort_order: number;
}

export interface CaseNote {
  id: string;
  case_id: string;
  author_id: string;
  body: string;
  case_review_step_id?: string | null;
  created_at: string;
}

/* ---------- Workflow actions ---------- */

export async function submitCase(caseId: string): Promise<CaseWorkflow> {
  const { data } = await apiClient.post<CaseWorkflow>(`/cases/${caseId}/submit`);
  return data;
}

export async function getCaseWorkflow(caseId: string): Promise<CaseWorkflow> {
  const { data } = await apiClient.get<CaseWorkflow>(`/cases/${caseId}/workflow`);
  return data;
}

export async function decideStep(
  caseId: string,
  stepId: string,
  decision: 'approve' | 'return' | 'escalate',
  rationale?: string,
): Promise<CaseWorkflow> {
  const { data } = await apiClient.post<CaseWorkflow>(
    `/cases/${caseId}/steps/${stepId}/decide`,
    { decision, rationale },
  );
  return data;
}

export async function returnCase(
  caseId: string,
  reason: string,
  missingChecklistItemIds: string[] = [],
): Promise<CaseWorkflow> {
  const { data } = await apiClient.post<CaseWorkflow>(
    `/cases/${caseId}/return`,
    { reason, missing_checklist_item_ids: missingChecklistItemIds },
  );
  return data;
}

export async function assignCase(
  caseId: string,
  assignedAnalystId: string,
): Promise<CaseWorkflow> {
  const { data } = await apiClient.post<CaseWorkflow>(
    `/cases/${caseId}/assign`,
    { assigned_analyst_id: assignedAnalystId },
  );
  return data;
}

/* ---------- Checklist ---------- */

export async function getChecklist(caseId: string): Promise<ChecklistItem[]> {
  const { data } = await apiClient.get<ChecklistItem[]>(`/cases/${caseId}/checklist`);
  return data;
}

export async function updateChecklistItem(
  caseId: string,
  itemId: string,
  input: { status?: ChecklistItem['status']; notes?: string },
): Promise<ChecklistItem> {
  const { data } = await apiClient.patch<ChecklistItem>(
    `/cases/${caseId}/checklist/${itemId}`,
    input,
  );
  return data;
}

/* ---------- Notes ---------- */

export async function getNotes(caseId: string): Promise<CaseNote[]> {
  const { data } = await apiClient.get<CaseNote[]>(`/cases/${caseId}/notes`);
  return data;
}

export async function addNote(
  caseId: string,
  body: string,
  caseReviewStepId?: string,
): Promise<CaseNote> {
  const { data } = await apiClient.post<CaseNote>(`/cases/${caseId}/notes`, {
    body,
    case_review_step_id: caseReviewStepId,
  });
  return data;
}

/* ---------- Admin workflow config ---------- */

export interface WorkflowListItem {
  id: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
  created_at?: string | null;
  steps: ReviewStepConfig[];
}

export async function getWorkflows(): Promise<WorkflowListItem[]> {
  const { data } = await apiClient.get<{ items: WorkflowListItem[] }>('/admin/workflows');
  return data.items;
}

export async function createWorkflow(input: {
  name: string;
  is_default?: boolean;
  is_active?: boolean;
}): Promise<WorkflowConfig> {
  const { data } = await apiClient.post<WorkflowConfig>('/admin/workflows', input);
  return data;
}

export async function updateWorkflow(
  id: string,
  input: { name?: string; is_default?: boolean; is_active?: boolean },
): Promise<WorkflowConfig> {
  const { data } = await apiClient.put<WorkflowConfig>(`/admin/workflows/${id}`, input);
  return data;
}

export async function addWorkflowStep(
  workflowId: string,
  input: {
    order: number;
    name: string;
    required_role: 'analyst' | 'reviewer' | 'principal' | 'compliance';
    required_approvals?: number;
    description?: string;
  },
): Promise<ReviewStepConfig> {
  const { data } = await apiClient.post<ReviewStepConfig>(
    `/admin/workflows/${workflowId}/steps`,
    input,
  );
  return data;
}